import { nodeDefinition } from './flowNodeCatalog';

export const summarize = (t, node) => {
  const config = node.config || {};
  switch (node.type) {
    case 'send_text':
      return config.text?.slice(0, 56) || t('FLOWS.NODES.SEND_TEXT.EMPTY');
    case 'wait':
      return t('FLOWS.NODES.WAIT.SUMMARY', { minutes: config.minutes || 0 });
    case 'condition':
      return `${config.attribute || '?'} ${config.operator || ''} ${
        config.value || ''
      }`.trim();
    case 'switch':
      return t('FLOWS.NODES.SWITCH.SUMMARY', {
        count: (config.cases || []).length,
      });
    case 'capture_input':
      return t('FLOWS.NODES.CAPTURE_INPUT.SUMMARY', {
        variable: config.variable || 'resposta',
      });
    case 'assign_agent': {
      const summaries = {
        last_responding: t('FLOWS.NODES.ASSIGN_AGENT.SUMMARY_LAST_RESPONDING'),
        specific: t('FLOWS.NODES.ASSIGN_AGENT.SUMMARY_SPECIFIC'),
        unassign: t('FLOWS.NODES.ASSIGN_AGENT.SUMMARY_UNASSIGN'),
      };

      return summaries[config.mode] || summaries.last_responding;
    }
    case 'set_status': {
      const labels = {
        open: t('FLOWS.CONFIG.STATUS_OPEN'),
        pending: t('FLOWS.CONFIG.STATUS_PENDING'),
        resolved: t('FLOWS.CONFIG.STATUS_RESOLVED'),
      };

      return labels[config.status] || labels.open;
    }
    case 'set_labels':
      return t('FLOWS.NODES.SET_LABELS.SUMMARY', {
        count: (config.labels || []).length,
      });
    case 'http_request': {
      let target = config.url?.trim() || '';
      try {
        target = new URL(target).host;
      } catch {
        // Keep the raw value visible while the URL is incomplete.
      }

      return `${(config.method || 'post').toUpperCase()} ${target}`.trim();
    }
    case 'ai_agent':
      return t('FLOWS.NODES.AI_AGENT.SUMMARY');
    case 'end':
      return config.reason || t('FLOWS.NODES.END.EMPTY');
    default:
      return '';
  }
};

export const toVueNode = (t, node, index) => {
  const definition = nodeDefinition(t, node.type);
  return {
    id: node.id,
    type: 'flowNode',
    position: node.position || { x: 80 + index * 280, y: 160 },
    data: {
      kind: node.type,
      label: definition.label,
      summary: summarize(t, node),
      config: node.config || definition.config,
      yesLabel: t('FLOWS.CONDITION.YES'),
      noLabel: t('FLOWS.CONDITION.NO'),
    },
  };
};

export const defaultGraph = t => ({
  nodes: [
    { id: 'start', type: 'start', config: {}, position: { x: 80, y: 180 } },
    {
      id: 'welcome',
      type: 'send_text',
      config: { text: t('FLOWS.BUILDER.DEFAULT_MESSAGE') },
      position: { x: 360, y: 180 },
    },
    { id: 'end', type: 'end', config: {}, position: { x: 640, y: 180 } },
  ],
  edges: [
    { id: 'start-welcome', source: 'start', target: 'welcome' },
    { id: 'welcome-end', source: 'welcome', target: 'end' },
  ],
});

export const graphPayload = (nodes, edges) => ({
  nodes: nodes.map(node => ({
    id: node.id,
    type: node.data.kind,
    config: node.data.config || {},
    position: node.position,
  })),
  edges: edges.map(edge => ({
    source: edge.source,
    target: edge.target,
    branch: edge.sourceHandle || null,
  })),
});

const isJsonObject = value => {
  if (!value) return true;
  if (typeof value !== 'string') {
    return typeof value === 'object' && !Array.isArray(value);
  }
  if (!value.trim()) return true;

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed);
  } catch {
    return false;
  }
};

const isValidResponseVariable = value =>
  !String(value || '').trim() ||
  /^[A-Za-z_][A-Za-z0-9_]*$/.test(String(value).trim());

const isValidVariableName = value =>
  /^[A-Za-z_][A-Za-z0-9_]*$/.test(String(value || '').trim());

const isValidHttpTimeout = value => {
  const timeout = Number(value);
  return Number.isInteger(timeout) && timeout >= 1 && timeout <= 30;
};

export const numberOrNull = value => {
  if (value === null || value === undefined || value === '') return null;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const isValidSendWindowHour = value =>
  value === null ||
  (Number.isInteger(Number(value)) &&
    Number(value) >= 0 &&
    Number(value) <= 23);

export const validationError = ({ t, name, nodes, edges, settings }) => {
  if (!name.trim()) return t('FLOWS.BUILDER.NAME_REQUIRED');
  if (
    !isValidSendWindowHour(numberOrNull(settings.send_window_start_hour)) ||
    !isValidSendWindowHour(numberOrNull(settings.send_window_end_hour))
  ) {
    return t('FLOWS.SETTINGS.SEND_WINDOW_INVALID');
  }

  const startNodes = nodes.filter(node => node.data.kind === 'start');
  const endNodes = nodes.filter(node => node.data.kind === 'end');
  if (startNodes.length !== 1) return t('FLOWS.BUILDER.START_REQUIRED');
  if (!endNodes.length) return t('FLOWS.BUILDER.END_REQUIRED');

  const agentNodeWithoutAgent = nodes.find(
    node =>
      node.data.kind === 'assign_agent' &&
      node.data.config?.mode === 'specific' &&
      !node.data.config?.agent_id
  );
  if (agentNodeWithoutAgent)
    return t('FLOWS.NODES.ASSIGN_AGENT.AGENT_REQUIRED');

  const labelsNodeWithoutLabels = nodes.find(
    node =>
      node.data.kind === 'set_labels' &&
      !(node.data.config?.labels || []).length
  );
  if (labelsNodeWithoutLabels) {
    return t('FLOWS.NODES.SET_LABELS.LABELS_REQUIRED');
  }

  const captureNodeWithoutVariable = nodes.find(
    node =>
      node.data.kind === 'capture_input' &&
      !isValidVariableName(node.data.config?.variable)
  );
  if (captureNodeWithoutVariable) {
    return t('FLOWS.NODES.CAPTURE_INPUT.VARIABLE_REQUIRED');
  }

  const aiNodeWithoutAgent = nodes.find(
    node => node.data.kind === 'ai_agent' && !node.data.config?.agent_id
  );
  if (aiNodeWithoutAgent) return t('FLOWS.NODES.AI_AGENT.AGENT_REQUIRED');

  const textNodeWithInvalidTemplate = nodes.find(
    node =>
      node.data.kind === 'send_text' &&
      node.data.config?.template_name &&
      !/^[A-Za-z0-9_]+$/.test(String(node.data.config.template_name).trim())
  );
  if (textNodeWithInvalidTemplate) {
    return t('FLOWS.NODES.SEND_TEXT.TEMPLATE_NAME_INVALID');
  }

  const httpNodeWithoutUrl = nodes.find(
    node => node.data.kind === 'http_request' && !node.data.config?.url?.trim()
  );
  if (httpNodeWithoutUrl) return t('FLOWS.NODES.HTTP_REQUEST.URL_REQUIRED');

  const httpNodeWithInvalidHeaders = nodes.find(
    node =>
      node.data.kind === 'http_request' &&
      !isJsonObject(node.data.config?.headers)
  );
  if (httpNodeWithInvalidHeaders) {
    return t('FLOWS.NODES.HTTP_REQUEST.HEADERS_INVALID');
  }

  const httpNodeWithInvalidVariable = nodes.find(
    node =>
      node.data.kind === 'http_request' &&
      !isValidResponseVariable(node.data.config?.response_variable)
  );
  if (httpNodeWithInvalidVariable) {
    return t('FLOWS.NODES.HTTP_REQUEST.RESPONSE_VARIABLE_INVALID');
  }

  const httpNodeWithInvalidTimeout = nodes.find(
    node =>
      node.data.kind === 'http_request' &&
      !isValidHttpTimeout(node.data.config?.timeout_seconds)
  );
  if (httpNodeWithInvalidTimeout) {
    return t('FLOWS.NODES.HTTP_REQUEST.TIMEOUT_INVALID');
  }

  const nodeWithoutOutput = nodes.find(node => {
    if (node.data.kind === 'end') return false;
    return !edges.some(edge => edge.source === node.id);
  });
  if (nodeWithoutOutput) {
    return t('FLOWS.BUILDER.OUTPUT_REQUIRED', {
      name: nodeWithoutOutput.data.label,
    });
  }

  return null;
};
